import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { firstValueFrom } from 'rxjs';

import { WindowService } from 'src/shared/providers/window.service';

import { ListingOptimizerJobStatus, ListingOptimizerReportResponse, SuggestionValue } from 'src/models/listing-optimizer.model';

@Injectable({
  providedIn: 'root'
})
export class ListingOptimizerService {
  private readonly CHAT_API_BASE = 'https://chat-api.tidy.com';
  private readonly PROXY_PATH = '/chat-api';

  private http = inject(HttpClient);
  private windowService = inject(WindowService);

  private get baseUrl(): string {
    const hostname = this.windowService.hostname;
    const useProxy =
      !Capacitor.isNativePlatform() &&
      (hostname === 'localhost' || hostname === '127.0.0.1');
    return useProxy ? this.PROXY_PATH : this.CHAT_API_BASE;
  }

  async submitAnalysis(params: { url: string; address_listing_id: number; email: string; address: string }): Promise<{ jobId: string }> {
    return firstValueFrom(
      this.http.post<{ jobId: string }>(`${this.baseUrl}/api/listing-optimizer`, params)
    );
  }

  async getJobStatus(jobId: string): Promise<ListingOptimizerJobStatus> {
    return firstValueFrom(
      this.http.get<ListingOptimizerJobStatus>(`${this.baseUrl}/api/listing-optimizer/jobs/${jobId}`)
    );
  }

  async getListingReport(listingId: string): Promise<ListingOptimizerReportResponse> {
    return firstValueFrom(
      this.http.get<ListingOptimizerReportResponse>(`${this.baseUrl}/api/listing-optimizer/listings/${listingId}/report`)
    );
  }

  async generateSuggestion(listingId: string, suggestionId: string): Promise<{ after: SuggestionValue }> {
    return firstValueFrom(
      this.http.post<{ after: SuggestionValue }>(
        `${this.baseUrl}/api/listing-optimizer/listings/${listingId}/suggestions/${suggestionId}/generate`,
        {}
      )
    );
  }

  buildShareLink(report: { listing?: { listingId?: string; listingUrl?: string } } | null): string {
    const listingId = report?.listing?.listingId;
    if (listingId) {
      return `https://www.tidy.com/rentals/listing-optimizer/results?listingId=${listingId}&shared=1`;
    }
    return report?.listing?.listingUrl || 'https://www.tidy.com/rentals/listing-optimizer';
  }

  // Generated photo URLs can come back relative (e.g. "/api/listing-optimizer/photos/...").
  // The browser would resolve those against the app host and 404, so prefix them with the
  // real chat-api host. Cross-origin <img> loads are unaffected by CORS.
  absoluteUrl(url: string | undefined | null): string {
    if (!url) {return '';}
    return url.startsWith('/') ? `${this.CHAT_API_BASE}${url}` : url;
  }
}
